import { Component, Event, EventEmitter, h, Host, Prop, State } from "@stencil/core";

export type TrumpfNotificationType = "Overview" | "Detail";
export type TrumpfNotificationStatus = "Current" | "Historic";
export type TrumpfNotificationSeverity = "Information" | "Warning" | "Error";

export interface TrumpfNotificationActionLink {
    link: string;
    linkText: string;
}

export interface TrumpfNotificationSource {
    source: string;
    sourceLink: string;
}

export interface TrumpfNotificationClosedBy {
    userName: string;
    label: string;
    closeButtonText: string;
}

@Component({
    tag: "ux-notification-item",
    styleUrl: "notification-item.scss",
    shadow: false
})
export class NotificationItem {
    private baseClass = "ux-notification-item";

    /** The headline of this notification */
    @Prop() readonly headline: string = "";

    /** seconds timestamp of UTC time when this notification was created. */
    @Prop() readonly createdAt: string = "";

    /** Text indicating when the notification will be deleted (e.g. 1h, 3d, etc.). */
    @Prop() readonly willDeleteIn: string = "";

    /** Text elaborating on the notification deletion. */
    @Prop() readonly willDeleteInTooltip: string = "";

    /** Time format string according to https://day.js.org/docs/en/display/format */
    @Prop() readonly timeFormat: string = "HH:mm";

    /** Date format string according to https://day.js.org/docs/en/display/format */
    @Prop() readonly dateFormat: string = "DD.MM.YYYY";

    /** Type of notification. Can be 'Overview' or 'Detail'. */
    @Prop() readonly type: TrumpfNotificationType = "Detail";

    /** Current status of this notification, can be 'Current' or 'Historic'. */
    @Prop() readonly status: TrumpfNotificationStatus = "Current";

    /** Severity of this notification. Can be 'Information', 'Warning' or 'Error'. */
    @Prop() readonly severity: TrumpfNotificationSeverity = "Information";

    /** Indicates where this notification was sourced from, e.g. TRUMPF Service */
    @Prop() readonly source: TrumpfNotificationSource["source"] = "";

    /** Source can also be a link, e.g. links to TRUMPF Service */
    @Prop() readonly sourceLink: TrumpfNotificationSource["sourceLink"] = "";

    /** User name of the user that changed the status of this notification to 'Historic'.
     *  Empty if the status of this notification is 'Current'. */
    @Prop() readonly closedByName: TrumpfNotificationClosedBy["userName"] = "";

    /** Label text for completety by */
    @Prop() readonly closedByLabel: TrumpfNotificationClosedBy["label"] = "Closed by";

    /** Close button text that leads to completion of notification */
    @Prop() readonly closeButtonText: TrumpfNotificationClosedBy["closeButtonText"] = "Close";

    /** Link of the notification, e.g. link for read more button */
    @Prop() readonly link: TrumpfNotificationActionLink["link"] = "";

    /** Link text of the notification, e.g. read more */
    @Prop() readonly linkText: TrumpfNotificationActionLink["linkText"] = "";

    /** Update notification url */
    @Prop() readonly notificationUpdateEndpoint: string = "";

    /** Update notification error text */
    @Prop() readonly notificationUpdateErrorText: string = "Update failed. Please try again.";

    /** Update notification state  */
    @State() notificationUpdateError = false;

    /** Navigation level change event */
    @Event({
        eventName: "uxUpdateNotificationItem",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    updateNotificationItem!: EventEmitter<{ success: boolean }>;

    /** Close notification */
    private closeNotification = async () => {
        this.notificationUpdateError = false;

        const response = await fetch(this.notificationUpdateEndpoint);
        response
            .json()
            .then(() => {
                this.notificationUpdateError = false;
                this.updateNotificationItem.emit({ success: true });
            })
            .catch(() => {
                this.notificationUpdateError = true;
                this.updateNotificationItem.emit({ success: false });
            });
    };

    render() {
        const {
            baseClass,
            headline,
            createdAt,
            willDeleteIn,
            willDeleteInTooltip,
            timeFormat,
            dateFormat,
            type,
            status,
            severity,
            source,
            sourceLink,
            closedByName,
            closedByLabel,
            closeButtonText,
            link,
            linkText,
            notificationUpdateError,
            notificationUpdateErrorText
        } = this;

        const utc = Number(createdAt) > 0 ? new Date(Number(createdAt) * 1000).toISOString() : "";

        return (
            <Host class={`${baseClass} ${baseClass}--${type.toLowerCase()} ${baseClass}--${severity.toLowerCase()}`}>
                <div class={`${baseClass}__icon-wrapper`}>
                    <ux-icon name={`notification-${severity.toLowerCase()}`} weight="bold" filled></ux-icon>
                    <div class={`${baseClass}__source-date`}>
                        {sourceLink && type.toLowerCase() === "detail" ? (
                            <a href={link} target="_self" class="ux-text-link ux-text-link--bold ux-text-link--green">
                                {source}
                                <ux-icon name="arrow-right" weight="normal" />
                            </a>
                        ) : (
                            <span class={`${baseClass}__source ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase`}>{source}</span>
                        )}
                        {!!utc && (
                            <span class={`${baseClass}__date-time ux-text-tiny`}>
                                <span class={`${baseClass}__time`}>
                                    <ux-date-time utc={utc} format={timeFormat} />
                                </span>
                                <span class={`${baseClass}__date`}>
                                    <ux-date-time utc={utc} format={dateFormat} />
                                </span>
                                {willDeleteIn && (
                                    <div class={`${baseClass}__will-delete-in`}>
                                        {willDeleteInTooltip && (
                                            <ux-tooltip tooltip-target="ux-icon[name='clock']" align="left">
                                                {willDeleteInTooltip}
                                            </ux-tooltip>
                                        )}
                                        <ux-icon name="clock" weight="bold" size-s="13" color="gray" /> {willDeleteIn}
                                    </div>
                                )}
                            </span>
                        )}
                    </div>
                </div>
                <ux-notification-text
                    subject={headline}
                    link={link}
                    linkText={linkText}
                    notificationUpdateError={notificationUpdateError}
                    notificationUpdateErrorText={notificationUpdateErrorText}
                    type={type}
                >
                    <slot name="message" />
                </ux-notification-text>
                <div class={`${baseClass}__status-wrapper`}>
                    {status.toLowerCase() === "current" && this.notificationUpdateEndpoint !== "" && (
                        <ux-popover-menu placement="bottom-end">
                            <ul>
                                <li onClick={() => this.closeNotification()}>{closeButtonText}</li>
                            </ul>
                        </ux-popover-menu>
                    )}
                    {status.toLowerCase() === "historic" && closedByName && (
                        <div>
                            <div class="ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase">{closedByLabel}:</div>
                            <div>{closedByName}</div>
                        </div>
                    )}
                </div>
            </Host>
        );
    }
}
